import {
  entityRefToUrn,
  generateURNByKind,
} from '@agilelab/plugin-wb-builder-common';
import {
  EntityIcon,
  WbAsyncSelect,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import { parseEntityRef, stringifyEntityRef } from '@mainstage/catalog-model';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@mainstage/integration-react';
import {
  catalogApiRef,
  entityRouteRef,
  getEntitySourceLocation,
} from '@mainstage/plugin-catalog-react';
import { Box, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import parseGitUrl from 'git-url-parse';
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { panelCatalogApiRef } from '../../../../api';
import { checkExistingEditTemplate } from '../../../common/utils';
import { useEditorPageContext } from '../../context/useEditorPageContext';
import { EntityWithIcon } from '../../types';
import {
  CARD_AVATAR_DIMENSION,
  CARD_HEIGHT,
  CARD_REDUCED_HEIGHT,
} from './const';
import { AlphaEntity, EntityStatus } from '@mainstage/catalog-model/alpha';
import { EntityCardMenu } from './EntityCardMenu';
import { EntityCardTitle } from './EntityCardTitle';

type StylesProps = { isActiveEntity?: boolean } | undefined;

const useStyles = makeStyles(theme => ({
  container: (props: StylesProps = { isActiveEntity: true }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    padding: '4px 8px',
    gap: '8px',
    minHeight: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    maxHeight: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    height: props?.isActiveEntity ? CARD_HEIGHT : CARD_REDUCED_HEIGHT,
    borderRadius: '4px',
    minWidth: 'fit-content',
  }),
  avatar: {
    width: CARD_AVATAR_DIMENSION,
    height: CARD_AVATAR_DIMENSION,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
  },
}));

interface Props {
  entity: EntityWithIcon;
  style?: CSSProperties;
  className?: string;
  isActiveEntity?: boolean;
  short?: boolean;
}

export const EntityCard: FC<Props> = ({
  entity,
  style,
  className,
  isActiveEntity,
  short = false,
}) => {
  const classes = useStyles({ isActiveEntity: isActiveEntity });

  const panelCatalog = useApi(panelCatalogApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    changeBranches,
    currRelease,
    editorRef,
    focusedEntityUrn,
    setFocusedEntityUrn,
    parent,
    descriptorStateWithComponents,
    fetchReleaseState,
    currentDescriptorViewMode,

    parsedDescriptor,
    customViewPageIndex,
    setCustomViewPageIndex,
  } = useEditorPageContext();

  const isCustomViewActive = useMemo(
    () => currentDescriptorViewMode === 'customView',
    [currentDescriptorViewMode],
  );

  const name: string = (entity?.spec?.mesh as any)?.name || '';
  const entityStatus: EntityStatus | undefined =
    (entity as AlphaEntity)?.status || undefined;
  const error: string | undefined =
    entityStatus?.items?.[0]?.error?.message || undefined;

  const [errorMessage, setErrorMessage] = useState('');
  const entitySourceLocation = useMemo(() => {
    return entity ? getEntitySourceLocation(entity, scmIntegrationsApi) : null;
  }, [entity, scmIntegrationsApi]);

  const entityUrn = generateURNByKind(entity.metadata.name, entity.kind);
  const entityRef = entity
    ? stringifyEntityRef({
        name: entity?.metadata?.name || '',
        kind: entity?.kind,
        namespace: entity?.metadata?.namespace || '',
      })
    : undefined;
  const entityRoute = useRouteRef(entityRouteRef);

  const defaultBranch = useMemo(() => {
    if (
      fetchReleaseState.loading ||
      !fetchReleaseState.value ||
      currRelease === null
    )
      return '';

    const createdFrom = currRelease?.metadata?.createdFrom?.find(
      f => f.entityRef === entityRef,
    );
    const branchFromCreatedFrom = createdFrom?.branch?.replace('%2F', '/');
    const branchFromLocationUrl = entitySourceLocation?.locationTargetUrl
      ? parseGitUrl(entitySourceLocation.locationTargetUrl).ref || 'master'
      : 'master';

    const rightBranch = branchFromCreatedFrom ?? branchFromLocationUrl;
    return rightBranch.replace('refs/heads/', '');
  }, [
    fetchReleaseState.loading,
    fetchReleaseState.value,
    currRelease,
    entitySourceLocation?.locationTargetUrl,
    entityRef,
  ]);

  const [targetBranch, setTargetBranch] = useState<string>(defaultBranch);

  /**
   * this effects sets the right default branch when the editor tab opens.
   * This is because `master` is not always the default branch, for example for
   * dp created using the `new version` feature. Another case is that, after a new draft release
   * is created from different branches, when refreshing the editor you want to see set as default
   * the branches from which that draft release was created.
   */
  useEffect(() => {
    if (defaultBranch !== '' && entityRef) {
      changeBranches(entityRef, defaultBranch);
      setTargetBranch(defaultBranch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBranch, entityRef]);

  const location = useLocation();

  const route = useMemo(() => {
    if (entity) {
      const data = {
        name: entity?.metadata.name ?? '',
        kind: entity?.kind,
        namespace: entity?.metadata.namespace ?? '',
      };
      const ref = parseEntityRef(data);
      const preUrl = location.pathname.includes('my-projects')
        ? '/my-projects'
        : '';

      return `${preUrl}${entityRoute(ref)}/editor`;
    }
    return '/';
  }, [entity, entityRoute, location.pathname]);

  const editorWizardRoute = useMemo(() => {
    if (!entity) return '#';
    const { namespace = 'default', name: entityName } = entity.metadata;
    const kindLowerCase = entity.kind.toLowerCase();

    const hrefEditorWizard = `/scaffolder/${encodeURIComponent(
      namespace,
    )}/${encodeURIComponent(kindLowerCase)}/${encodeURIComponent(
      entityName,
    )}/editor-wizard?targetBranch=${targetBranch}`;

    return hrefEditorWizard;
  }, [entity, targetBranch]);

  useEffect(() => {
    checkExistingEditTemplate({ catalogApi, entity, setErrorMessage });
  }, [entity, setErrorMessage, catalogApi]);

  const getBranches = useCallback(async () => {
    const result = await panelCatalog.getBranches(
      entitySourceLocation?.locationTargetUrl!,
    );

    return Object.keys(result);
  }, [entitySourceLocation?.locationTargetUrl, panelCatalog]);

  const highlightHandleClick = useCallback(() => {
    if (editorRef?.current) {
      const entityIdLine = editorRef.current.findLineContainingId(entityUrn);
      const lineRanges =
        editorRef.current.selectLinesOfOtherSections(entityUrn);

      if (lineRanges.ranges()?.length !== 0) {
        setFocusedEntityUrn(entityUrn);
        editorRef.current.highlightLineRanges(lineRanges, 'blur-line');
        editorRef.current.goToLine(entityIdLine, false, 200);
      }
    }
  }, [editorRef, entityUrn, setFocusedEntityUrn]);

  useEffect(() => {
    // check if the entity is the active one, if is a component so has a parent and the descriptor is not loading.
    let dispose: (() => void) | undefined;
    if (isActiveEntity && parent && !descriptorStateWithComponents.loading) {
      // verifies if the state value has been propagated to the inner state of monaco editor
      const isContentReady = () => {
        return (
          descriptorStateWithComponents.value === editorRef?.current?.getValue()
        );
      };

      const apply = () => {
        if (isContentReady()) {
          highlightHandleClick();
          dispose?.();
        }
      };

      if (isContentReady()) {
        highlightHandleClick();
      }
      // if inner monaco value still not updated,
      // subscribe to onDidChangeModelContent waiting for monaco to update it (in the callback we highlight), also stores the dispose function for later cleanup
      else
        dispose = editorRef?.current?.onDidChangeModelContent(apply)?.dispose;
    }
    return () => {
      dispose?.();
    };
  }, [
    descriptorStateWithComponents.loading,
    descriptorStateWithComponents.value,
    isActiveEntity,
    editorRef,
    highlightHandleClick,
    parent,
  ]);

  // Auto-navigate to this component in custom view when the descriptor first loads
  const prevParsedDescriptor = useRef(parsedDescriptor);
  useEffect(() => {
    const wasNull = prevParsedDescriptor.current === null;
    prevParsedDescriptor.current = parsedDescriptor;
    if (
      wasNull &&
      parsedDescriptor !== null &&
      isActiveEntity &&
      parent &&
      isCustomViewActive
    ) {
      const comps: any[] = parsedDescriptor.components ?? [];
      const idx = comps.findIndex(c => c.id === entityUrn);
      if (idx !== -1) setCustomViewPageIndex(idx + 1);
    }
  }, [
    parsedDescriptor,
    isActiveEntity,
    parent,
    isCustomViewActive,
    entityUrn,
    setCustomViewPageIndex,
  ]);

  const locationTargetUrl = useMemo(() => {
    const str = entitySourceLocation?.locationTargetUrl;
    const lastIndex = str?.lastIndexOf(defaultBranch);
    if (lastIndex === undefined || lastIndex === -1) {
      return str;
    }
    return `${str?.substring(0, lastIndex)}${targetBranch}${str?.substring(
      lastIndex + defaultBranch.length,
    )}`;
  }, [entitySourceLocation?.locationTargetUrl, targetBranch, defaultBranch]);

  const componentIndexInDescriptor = useMemo(
    () =>
      (parsedDescriptor?.components ?? []).findIndex(
        (c: any) => c.id === entityUrn,
      ),
    [parsedDescriptor, entityUrn],
  );

  const onFocusToggle = useCallback(() => {
    if (isCustomViewActive) {
      const targetPage =
        componentIndexInDescriptor !== -1 ? componentIndexInDescriptor + 1 : 0;
      // toggle: if already on this page, go back to system (0)
      setCustomViewPageIndex(prev => (prev === targetPage ? 0 : targetPage));
      return;
    }

    if (focusedEntityUrn === entityUrn) {
      setFocusedEntityUrn(null);
      editorRef?.current?.highlightLines([]);
      return;
    }
    highlightHandleClick();
  }, [
    isCustomViewActive,
    componentIndexInDescriptor,
    setCustomViewPageIndex,
    editorRef,
    focusedEntityUrn,
    entityUrn,
    highlightHandleClick,
    setFocusedEntityUrn,
  ]);

  let focused: boolean;
  if (isCustomViewActive) {
    if (componentIndexInDescriptor !== -1) {
      focused = customViewPageIndex === componentIndexInDescriptor + 1;
    } else {
      focused = customViewPageIndex === 0;
    }
  } else {
    focused = entityUrn === focusedEntityUrn;
  }

  const taxonomyRef = (entity?.metadata?.classDetails as any).taxonomy;

  const taxonomyUrn = useMemo(() => {
    try {
      return entityRefToUrn(taxonomyRef);
    } catch (_e) {
      return undefined;
    }
  }, [taxonomyRef]);
  return (
    <Box style={style} className={clsx(classes.container, className)}>
      <EntityIcon
        title={name}
        className={classes.avatar}
        useCaseTemplateId={(entity?.spec?.mesh as any)?.useCaseTemplateId}
        kind={entity.kind.toLowerCase()}
        taxonomyUrn={taxonomyUrn}
        style={{ flexShrink: 0 }}
        disableSystemForceDefault
      />
      <Box style={{ flex: 1 }} className={classes.column}>
        <EntityCardTitle
          name={name}
          loading={descriptorStateWithComponents.loading}
          route={route}
          error={error}
        />

        {isActiveEntity && (
          <WbAsyncSelect<string>
            field="Branch"
            compact={short}
            defaultValue={defaultBranch}
            onChange={value => {
              changeBranches(entityRef!, value);
              setTargetBranch(value);
              setFocusedEntityUrn(null);
            }}
            disabled={descriptorStateWithComponents.loading}
            getOptions={() => getBranches()}
            renderOption={o => (
              <WbTruncatedTypographyWrapper
                style={{ display: 'inline' }}
                value={o}
              />
            )}
            renderValue={o => o}
            onSearch={(v, op) => op.filter(o => new RegExp(v, 'ig').test(o))}
            closeOnSelect
          />
        )}
      </Box>
      <Box style={{ display: 'flex', gap: '2px' }}>
        <EntityCardMenu
          short={short}
          focused={focused}
          onFocusToggle={onFocusToggle}
          editorWizardRoute={editorWizardRoute}
          locationTargetUrl={locationTargetUrl}
          errorMessage={errorMessage}
          isLoading={descriptorStateWithComponents.loading}
        />
      </Box>
    </Box>
  );
};
